import React from "react";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  SvgIcon,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { ArrowBack as ArrowBackIcon, Edit as EditIcon } from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import YouTube from "react-youtube";
import { useHistory, useLocation } from "react-router-dom";
import BibleReferenceButton from "components/BibleReference/BibleReferenceButton";
import { UserContext } from "contexts/UserContext";

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.background.paper,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
  },
  title: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
    overflow: "hidden",
  },
  content: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  youtubeBox: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
  actionButtons: {
    marginBottom: theme.spacing(1),
  },
  spotifyButton: {
    transition: "0.2s ease-in-out",
    "&:hover": {
      color: "#1DB954",
    },
  },
  lyrics: {
    whiteSpace: "pre-wrap",
  },
}));

export default ({ song, goBack }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const userContext = React.useContext(UserContext);
  // Same breakpoint EventDetailPage.jsx uses - desktop has the room to
  // show lyrics and the video at a wider column than mobile ever would,
  // so only widen it there.
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  // Same gate SongMenuButton's own "Edit" menu item and every other
  // song-mutating action in the app already use.
  const canEdit = !!(userContext.user.is_team_leader | userContext.user.is_staff);

  if (song.notFound) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={8} px={2} textAlign="center">
        <Typography variant="h6" gutterBottom>
          Can't find this song
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          It may belong to a different team than the one you're currently on.
        </Typography>
        <Box mt={2}>
          <Button variant="outlined" onClick={goBack}>
            Go back
          </Button>
        </Box>
      </Box>
    );
  }

  if (song.loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <>
      <AppBar position="sticky" color="default" className={classes.appBar} elevation={1}>
        <Toolbar className={classes.toolbar}>
          <IconButton edge="start" aria-label="back" title="Back" onClick={goBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" className={classes.title} noWrap>
            {song.title}
            {song.artist ? ` - ${song.artist}` : ""}
          </Typography>
          {canEdit && (
            <IconButton
              edge="end"
              aria-label="Edit"
              title="Edit"
              onClick={() => history.push(`/songs/${song.id}/edit`, { from: location })}
            >
              <EditIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth={isDesktop ? undefined : "md"} className={classes.content}>
        {song.youtube_id && (
          <Box className={classes.youtubeBox}>
            <YouTube
              videoId={song.youtube_id}
              opts={{
                width: "100%",
                playerVars: { start: song.youtube_start || 0, playsinline: 1 },
              }}
            />
          </Box>
        )}
        <Box className={classes.actionButtons}>
          <BibleReferenceButton song={song} />
          {song.spotify_id && (
            <IconButton
              href={`spotify:track:${song.spotify_id}`}
              title="Open in Spotify"
              className={classes.spotifyButton}
            >
              <SvgIcon>
                <path d="M19.098 10.638c-3.868-2.297-10.248-2.508-13.941-1.387-.593.18-1.22-.155-1.399-.748-.18-.593.154-1.22.748-1.4 4.239-1.287 11.285-1.038 15.738 1.605.533.317.708 1.005.392 1.538-.316.533-1.005.709-1.538.392zm-.126 3.403c-.272.44-.847.578-1.287.308-3.225-1.982-8.142-2.557-11.958-1.399-.494.15-1.017-.129-1.167-.623-.149-.495.13-1.016.624-1.167 4.358-1.322 9.776-.682 13.48 1.595.44.27.578.847.308 1.286zm-1.469 3.267c-.215.354-.676.465-1.028.249-2.818-1.722-6.365-2.111-10.542-1.157-.402.092-.803-.16-.895-.562-.092-.403.159-.804.562-.896 4.571-1.045 8.492-.595 11.655 1.338.353.215.464.676.248 1.028zm-5.503-17.308c-6.627 0-12 5.373-12 12 0 6.628 5.373 12 12 12 6.628 0 12-5.372 12-12 0-6.627-5.372-12-12-12z" />
              </SvgIcon>
            </IconButton>
          )}
        </Box>
        <Typography className={classes.lyrics}>{song.lyrics || ""}</Typography>
      </Container>
    </>
  );
};
